import { useState, useEffect, useRef } from 'react';

type TimerPhase = 'delay' | 'active' | 'completed';

interface UseSunbedTimerProps {
    initialRemaining: number; // in seconds
    initialDelay?: number;    // in seconds
    onComplete?: () => void;
    onPhaseChange?: (phase: TimerPhase) => void;
    isActive?: boolean;       // externally control if timer is running (e.g. paused)
}

export const useSunbedTimer = ({
    initialRemaining,
    initialDelay = 0,
    onComplete,
    onPhaseChange,
    isActive = true
}: UseSunbedTimerProps) => {
    const [phase, setPhase] = useState<TimerPhase>(() => {
        if (initialDelay > 0) return 'delay';
        if (initialRemaining > 0) return 'active';
        return 'completed';
    });

    const [delayLeft, setDelayLeft] = useState(initialDelay);
    const [secondsLeft, setSecondsLeft] = useState(initialRemaining);

    // Refs to avoid dependency cycles in effect
    const onCompleteRef = useRef(onComplete);
    const onPhaseChangeRef = useRef(onPhaseChange);

    useEffect(() => {
        onCompleteRef.current = onComplete;
        onPhaseChangeRef.current = onPhaseChange;
    }, [onComplete, onPhaseChange]);

    useEffect(() => {
        if (!isActive) return;
        if (phase === 'completed') return;

        const tick = () => {
            if (phase === 'delay') {
                setDelayLeft((prev) => {
                    const newValue = prev - 1;
                    if (newValue <= 0) {
                        setPhase('active');
                        onPhaseChangeRef.current?.('active');
                        return 0;
                    }
                    return newValue;
                });
            } else if (phase === 'active') {
                setSecondsLeft((prev) => {
                    const newValue = prev - 1;
                    if (newValue <= 0) {
                        setPhase('completed');
                        onPhaseChangeRef.current?.('completed');
                        onCompleteRef.current?.();
                        return 0;
                    }
                    return newValue;
                });
            }
        };

        const intervalId = setInterval(tick, 1000);
        return () => clearInterval(intervalId);
    }, [phase, isActive]);

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    return {
        phase,
        delayLeft,
        secondsLeft,
        displayTime: phase === 'delay' ? formatTime(delayLeft) : formatTime(secondsLeft),
        isCompleted: phase === 'completed'
    };
};
